/**
 * Endpoint Form Component
 * Form for creating and editing REST endpoints
 */

import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { RestEndpoint, Connection } from '../../types';
import { connectionsApi } from '../../api/client';

interface EndpointFormProps {
  endpoint?: RestEndpoint | null;
  onSubmit: (data: Partial<RestEndpoint>) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
}

export function EndpointForm({ endpoint, onSubmit, onCancel, isSubmitting }: EndpointFormProps) {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    base_url: '',
    path: '',
    method: 'GET',
    supports_odata: true,
    odata_entity_set: '',
    connection_id: '' as string,
    default_page_size: 50,
    refresh_interval_seconds: 300,
    max_records: 1000,  // Maximum records to fetch (0 = unlimited)
  });

  const { data: connections } = useQuery({
    queryKey: ['connections'],
    queryFn: connectionsApi.list,
  });

  useEffect(() => {
    if (endpoint) {
      setFormData({
        name: endpoint.name || '',
        description: endpoint.description || '',
        base_url: endpoint.base_url || '',
        path: endpoint.path || '',
        method: endpoint.method || 'GET',
        supports_odata: endpoint.supports_odata ?? true,
        odata_entity_set: endpoint.odata_entity_set || '',
        connection_id: endpoint.connection_id || '',
        default_page_size: endpoint.default_page_size || 50,
        refresh_interval_seconds: endpoint.refresh_interval_seconds || 300,
        max_records: endpoint.max_records ?? 1000,
      });
    }
  }, [endpoint]);

  const handleConnectionChange = (connectionId: string) => {
    const conn = connections?.find(c => c.id === connectionId);
    if (conn) {
      setFormData(prev => ({
        ...prev,
        connection_id: conn.id,
        base_url: conn.base_url,
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        connection_id: '',
      }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      ...formData,
      connection_id: formData.connection_id || null,  // UUID string or null
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Name */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Name *
        </label>
        <input
          type="text"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#45bdd6] focus:border-blue-500"
          placeholder="e.g. Production Orders"
          required
        />
      </div>

      {/* Description */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Description
        </label>
        <textarea
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#45bdd6] focus:border-blue-500"
          rows={2}
          placeholder="Optional description"
        />
      </div>

      {/* Connection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Connection
        </label>
        <select
          value={formData.connection_id}
          onChange={(e) => handleConnectionChange(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#45bdd6] focus:border-blue-500"
        >
          <option value="">-- No Connection --</option>
          {connections?.map((conn) => (
            <option key={conn.id} value={conn.id}>
              {conn.name} ({conn.auth_type})
            </option>
          ))}
        </select>
      </div>

      {/* Base URL */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Base URL *
        </label>
        <input
          type="url"
          value={formData.base_url}
          onChange={(e) => setFormData({ ...formData, base_url: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#45bdd6] focus:border-blue-500"
          placeholder="https://api.example.com"
          required
        />
      </div>

      {/* Path */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Path *
        </label>
        <input
          type="text"
          value={formData.path}
          onChange={(e) => setFormData({ ...formData, path: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#45bdd6] focus:border-blue-500"
          placeholder="/TENANT/LN/lnapi/odata/..."
          required
        />
      </div>

      {/* Method */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          HTTP Method
        </label>
        <select
          value={formData.method}
          onChange={(e) => setFormData({ ...formData, method: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#45bdd6] focus:border-blue-500"
        >
          <option value="GET">GET</option>
          <option value="POST">POST</option>
        </select>
      </div>

      {/* OData Settings */}
      <div className="border border-gray-200 rounded-lg p-4 space-y-3">
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="supports_odata"
            checked={formData.supports_odata}
            onChange={(e) => setFormData({ ...formData, supports_odata: e.target.checked })}
            className="w-4 h-4 text-[#45bdd6] border-gray-300 rounded focus:ring-[#45bdd6]"
          />
          <label htmlFor="supports_odata" className="text-sm font-medium text-gray-700">
            Supports OData
          </label>
        </div>

        {formData.supports_odata && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              OData Entity Set
            </label>
            <input
              type="text"
              value={formData.odata_entity_set}
              onChange={(e) => setFormData({ ...formData, odata_entity_set: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#45bdd6] focus:border-blue-500"
              placeholder="e.g. Orders"
            />
          </div>
        )}
      </div>

      {/* Configuration */}
      <div className="grid grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Page Size
          </label>
          <input
            type="number"
            value={formData.default_page_size}
            onChange={(e) => setFormData({ ...formData, default_page_size: Number(e.target.value) })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#45bdd6] focus:border-blue-500"
            min={1}
            max={1000}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Max Records
          </label>
          <input
            type="number"
            value={formData.max_records}
            onChange={(e) => setFormData({ ...formData, max_records: Number(e.target.value) })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#45bdd6] focus:border-blue-500"
            min={0}
            placeholder="0 = unlimited"
            title="Maximum records to fetch across all pages (0 = unlimited)"
          />
          <p className="text-xs text-gray-500 mt-1">0 = unlimited</p>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Refresh Interval (sec.)
          </label>
          <input
            type="number"
            value={formData.refresh_interval_seconds}
            onChange={(e) => setFormData({ ...formData, refresh_interval_seconds: Number(e.target.value) })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#45bdd6] focus:border-blue-500"
            min={30}
          />
        </div>
      </div>

      {/* Buttons */}
      <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          disabled={isSubmitting}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="px-4 py-2 bg-[#45bdd6] text-white rounded-lg hover:bg-[#3aa8bf] transition-colors disabled:opacity-50"
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Saving...' : endpoint ? 'Update' : 'Create'}
        </button>
      </div>
    </form>
  );
}
